import { Alert, Button, Card, Col, DatePicker, Descriptions, Input, Modal, Row, Select, Space, Table, Tag, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { apiCall } from '../../API/RestApi';
import '../ApprovalAttendance/ApprovalToolbar.css';

const { Text } = Typography;

function ApprovalUserLeavePaid({ embedded = false }) {
    const token = useSelector((state) => state.user.token);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('Pending');
    const [search, setSearch] = useState('');
    const [detail, setDetail] = useState(null);
    const [rejectModal, setRejectModal] = useState({ open: false, record: null });
    const [rejectReason, setRejectReason] = useState('');
    const [processingId, setProcessingId] = useState(null);
    const [accessDenied, setAccessDenied] = useState(false);
    const [dateRange, setDateRange] = useState([]);

    const getData = () => {
        setLoading(true);
        setAccessDenied(false);

        apiCall({
            method: 'GET',
            url: `/userleavepaidapproval?status=${statusFilter}`,
            data: {
                headers: { Authorization: `Bearer ${token}` },
            },
        }).then((res) => {
            if (res?.status === 'success') {
                setData(Array.isArray(res.data) ? res.data : []);
            } else {
                setData([]);
                if ((res?.message || '').toLowerCase().includes('tidak memiliki akses')) {
                    setAccessDenied(true);
                } else {
                    Modal.error({
                        title: 'Gagal mengambil data approval',
                        content: res?.message || res?.msg || 'Terjadi kesalahan saat mengambil data approval.',
                    });
                }
            }
            setLoading(false);
        }).catch(() => {
            setLoading(false);
            setData([]);
            Modal.error({
                title: 'Gagal mengambil data approval',
                content: 'Terjadi kesalahan saat mengambil data approval.',
            });
        });
    };

    useEffect(() => {
        getData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, token]);

    const filteredData = useMemo(() => {
        const keyword = search.trim().toLowerCase();
        return data.filter((item) => {
            const matchKeyword = !keyword || [
                item.user_name,
                item.requested_by_name,
                item.users_id,
                item.user_leave_paid_request_id,
                item.action,
                item.notes,
            ].some((value) => String(value ?? '').toLowerCase().includes(keyword));

            const requestedAt = item.requested_at ? moment(item.requested_at) : null;
            const matchDate = dateRange?.length !== 2 || (
                requestedAt &&
                requestedAt.isSameOrAfter(dateRange[0].startOf('day')) &&
                requestedAt.isSameOrBefore(dateRange[1].endOf('day'))
            );
            return matchKeyword && matchDate;
        });
    }, [data, search, dateRange]);

    const processApproval = (record, status, reason = '') => {
        const requestId = record.user_leave_paid_request_id;
        setProcessingId(requestId);

        apiCall({
            method: 'PUT',
            url: `/userleavepaidapproval/${requestId}`,
            data: {
                data: { status, reason },
                headers: { Authorization: `Bearer ${token}` },
            },
        }).then((res) => {
            setProcessingId(null);

            if (res?.status === 'success') {
                setRejectModal({ open: false, record: null });
                setRejectReason('');
                setDetail(null);
                Modal.success({
                    title: status === 'Approved' ? 'Approval berhasil' : 'Request ditolak',
                    content: res?.message || 'Request berhasil diproses.',
                });
                getData();
                return;
            }

            Modal.error({
                title: 'Gagal memproses approval',
                content: res?.message || res?.msg || 'Request tidak dapat diproses.',
            });
        }).catch(() => {
            setProcessingId(null);
            Modal.error({
                title: 'Gagal memproses approval',
                content: 'Terjadi kesalahan saat memproses approval.',
            });
        });
    };

    const confirmApprove = (record) => {
        Modal.confirm({
            title: 'Approve perubahan cuti?',
            content: `Perubahan cuti untuk ${record.user_name || `user ${record.users_id}`} akan langsung diterapkan.`,
            okText: 'Approve',
            cancelText: 'Batal',
            onOk: () => processApproval(record, 'Approved'),
        });
    };

    const statusTag = (status) => {
        const color = status === 'Approved' ? 'green' : status === 'Rejected' ? 'red' : 'orange';
        return <Tag color={color}>{status}</Tag>;
    };

    const columns = [
        {
            title: 'No',
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Employee',
            width: 220,
            sorter: (a, b) => String(a.user_name || '').localeCompare(String(b.user_name || '')),
            render: (_, record) => (
                <div>
                    <div><b>{record.user_name || '-'}</b></div>
                    <Text type="secondary">ID: {record.users_id}</Text>
                </div>
            ),
        },
        {
            title: 'Request',
            width: 120,
            render: (_, record) => (
                <Space direction="vertical" size={2}>
                    <Tag color={record.action === 'CREATE' ? 'green' : 'blue'}>{record.action}</Tag>
                    <Text type="secondary">#{record.user_leave_paid_request_id}</Text>
                </Space>
            ),
        },
        { title: 'Default', dataIndex: 'leave_paid_default', width: 90 },
        { title: 'Bonus', dataIndex: 'leave_obtained', width: 80 },
        { title: 'Used', dataIndex: 'leave_used', width: 80 },
        { title: 'Cut', dataIndex: 'cut_leave', width: 80 },
        {
            title: 'Period',
            width: 210,
            sorter: (a, b) => String(a.date_start || '').localeCompare(String(b.date_start || '')),
            render: (_, record) => {
                const start = record.date_start ? moment(record.date_start).format('DD-MM-YYYY') : '-';
                const end = record.date_end ? moment(record.date_end).format('DD-MM-YYYY') : '-';
                return `${start} s/d ${end}`;
            },
        },
        {
            title: 'Requested By',
            width: 180,
            sorter: (a, b) => String(a.requested_at || '').localeCompare(String(b.requested_at || '')),
            render: (_, record) => (
                <div>
                    <div>{record.requested_by_name || '-'}</div>
                    <Text type="secondary">
                        {record.requested_at ? moment(record.requested_at).format('DD-MM-YYYY HH:mm') : '-'}
                    </Text>
                </div>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            width: 110,
            sorter: (a, b) => String(a.status || '').localeCompare(String(b.status || '')),
            render: statusTag,
        },
        {
            title: 'Action',
            fixed: 'right',
            width: statusFilter === 'Pending' ? 250 : 100,
            render: (_, record) => (
                <Space>
                    <Button icon={<EyeOutlined />} onClick={() => setDetail(record)}>
                        Detail
                    </Button>
                    {statusFilter === 'Pending' && (
                        <>
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                loading={processingId === record.user_leave_paid_request_id}
                                onClick={() => confirmApprove(record)}
                            >
                                Approve
                            </Button>
                            <Button
                                danger
                                icon={<CloseOutlined />}
                                disabled={processingId === record.user_leave_paid_request_id}
                                onClick={() => {
                                    setRejectReason('');
                                    setRejectModal({ open: true, record });
                                }}
                            >
                                Reject
                            </Button>
                        </>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            {!embedded && <Helmet><title>Time Off Balance Approval</title></Helmet>}

            {!embedded && <h3>Time Off Balance Approval</h3>}

            <Card>
                <div className="approval-toolbar">
                    <div className="approval-toolbar__search">
                        <Input.Search
                            allowClear
                            placeholder="Search employee / requester / request ID"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="approval-toolbar__date">
                        <DatePicker.RangePicker
                            style={{ width: '100%' }}
                            value={dateRange}
                            format="DD-MM-YYYY"
                            placeholder={['Start date', 'End date']}
                            onChange={(value) => setDateRange(value || [])}
                        />
                    </div>
                    <div className="approval-toolbar__filter">
                        <Select
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{ width: '100%' }}
                            options={[
                                { value: 'Pending', label: 'Pending' },
                                { value: 'Approved', label: 'Approved' },
                                { value: 'Rejected', label: 'Rejected' },
                            ]}
                        />
                    </div>
                    <div className="approval-toolbar__action approval-toolbar__action--compact">
                        <Button block icon={<ReloadOutlined />} onClick={getData} loading={loading}>
                            Refresh
                        </Button>
                    </div>
                </div>

                {accessDenied && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Tidak memiliki akses"
                        description="Halaman ini hanya dapat digunakan oleh user yang terdaftar sebagai approver Time Off Balance."
                    />
                )}

                <Table
                    rowKey="user_leave_paid_request_id"
                    loading={loading}
                    dataSource={filteredData}
                    columns={columns}
                    scroll={{ x: 1450 }}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                />
            </Card>

            <Modal
                title="Detail Time Off Balance Request"
                open={!!detail}
                onCancel={() => setDetail(null)}
                footer={detail?.status === 'Pending' ? [
                    <Button key="reject" danger onClick={() => {
                        setRejectReason('');
                        setRejectModal({ open: true, record: detail });
                    }}>
                        Reject
                    </Button>,
                    <Button key="approve" type="primary" onClick={() => confirmApprove(detail)}>
                        Approve
                    </Button>,
                ] : null}
                width={760}
            >
                {detail && (
                    <Descriptions bordered column={2} size="small">
                        <Descriptions.Item label="Request ID">#{detail.user_leave_paid_request_id}</Descriptions.Item>
                        <Descriptions.Item label="Status">{statusTag(detail.status)}</Descriptions.Item>
                        <Descriptions.Item label="Employee">{detail.user_name || '-'}</Descriptions.Item>
                        <Descriptions.Item label="User ID">{detail.users_id}</Descriptions.Item>
                        <Descriptions.Item label="Action">
                            <Tag color={detail.action === 'CREATE' ? 'green' : 'blue'}>{detail.action}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Requested By">{detail.requested_by_name || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Default">{detail.leave_paid_default}</Descriptions.Item>
                        <Descriptions.Item label="Bonus">{detail.leave_obtained}</Descriptions.Item>
                        <Descriptions.Item label="Used">{detail.leave_used}</Descriptions.Item>
                        <Descriptions.Item label="Cut">{detail.cut_leave}</Descriptions.Item>
                        <Descriptions.Item label="Start Date">
                            {detail.date_start ? moment(detail.date_start).format('DD-MM-YYYY') : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="End Date">
                            {detail.date_end ? moment(detail.date_end).format('DD-MM-YYYY') : '-'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Notes" span={2}>{detail.notes || '-'}</Descriptions.Item>
                        <Descriptions.Item label="Requested At" span={2}>
                            {detail.requested_at ? moment(detail.requested_at).format('DD-MM-YYYY HH:mm:ss') : '-'}
                        </Descriptions.Item>
                        {detail.approved_by_name && detail.approved_by_name !== '-' && (
                            <Descriptions.Item label="Processed By" span={2}>{detail.approved_by_name}</Descriptions.Item>
                        )}
                        {detail.reason && (
                            <Descriptions.Item label="Reason" span={2}>{detail.reason}</Descriptions.Item>
                        )}
                    </Descriptions>
                )}
            </Modal>

            <Modal
                title="Reject Time Off Balance Request"
                open={rejectModal.open}
                okText="Reject"
                okButtonProps={{ danger: true, disabled: !rejectReason.trim(), loading: processingId === rejectModal.record?.user_leave_paid_request_id }}
                onCancel={() => {
                    setRejectModal({ open: false, record: null });
                    setRejectReason('');
                }}
                onOk={() => processApproval(rejectModal.record, 'Rejected', rejectReason.trim())}
            >
                <Input.TextArea
                    rows={4}
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder="Masukkan alasan penolakan"
                />
            </Modal>
        </>
    );
}

export default ApprovalUserLeavePaid;
